/**
 * The glossary — every commercial insurance term, in plain English.
 *
 * Josh, 2026-09-05: "as comprehensive as possible of as many commercial
 * insurance terms and phrases as possible. This portal is to be used by brand
 * new to commercial agents and seasoned vets."
 *
 * ⚠️ IT SERVES BOTH READERS FROM THE SAME ENTRY RATHER THAN HAVING TWO MODES.
 * Every term opens with one plain sentence, and then — where there is one — a
 * second block for the part that actually bites, and a third for the term it
 * gets mistaken for. A new agent stops after the first sentence and is not
 * slowed down; a twenty-year producer skips it and reads the second. Splitting
 * the page into "basic" and "advanced" would have made the vet's version the
 * one nobody maintains.
 *
 * ⚠️ SEARCH SHOWS A RANKED FLAT LIST; BROWSING SHOWS LETTER GROUPS. Two
 * different activities. Someone typing "EMR" wants the best answer at the top;
 * someone clicking "Workers' comp" wants the order their eye expects. Ranking a
 * browse makes the list reshuffle every time a filter changes.
 *
 * ⚠️ EVERY TERM IS ADDRESSABLE — /hub/glossary#additional-insured. Other pages
 * and Connor will link into this, and a glossary you can only search is one
 * nobody can point at. The ids are permanent for that reason; see
 * packages/shared/src/glossary/types.ts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import {
  CATEGORY_LABEL,
  GLOSSARY,
  GLOSSARY_CATEGORIES,
  GLOSSARY_LETTERS,
  glossarySeeAlso,
  glossaryTerm,
  searchGlossary,
  type GlossaryTerm,
} from '@commpass/shared';

const ESSENTIAL_COUNT = GLOSSARY.filter((t) => t.essential).length;

/** Counts per category, computed once — they never change at runtime. */
const CATEGORY_COUNTS: Record<string, number> = GLOSSARY.reduce<Record<string, number>>((acc, t) => {
  acc[t.category] = (acc[t.category] ?? 0) + 1;
  return acc;
}, {});

/**
 * One entry.
 *
 * ⚠️ THE THREE BLOCKS ARE LABELLED, NOT RUN TOGETHER. "In practice" and "Often
 * confused with" are doing different jobs from the definition, and an unlabelled
 * third paragraph reads as more definition — which is exactly how somebody ends
 * up quoting the caveat to a client as though it were the rule.
 */
function Entry({
  term,
  onPick,
  flash,
}: {
  term: GlossaryTerm;
  onPick: (id: string) => void;
  flash: boolean;
}): JSX.Element {
  const related = glossarySeeAlso(term);
  return (
    <article className={`gl-entry${flash ? ' is-flash' : ''}`} id={term.id}>
      <header>
        <h3>
          {term.term}
          {term.short && <span className="gl-abbr">{term.short}</span>}
        </h3>
        <span className="gl-cat">{CATEGORY_LABEL[term.category]}</span>
        {term.essential && <span className="gl-star">Start here</span>}
      </header>

      <p className="gl-def">{term.def}</p>

      {term.detail && (
        <div className="gl-note">
          <b>In practice</b>
          <p>{term.detail}</p>
        </div>
      )}

      {term.confused && (
        <div className="gl-note is-confused">
          <b>Often confused with</b>
          <p>{term.confused}</p>
        </div>
      )}

      {related.length > 0 && (
        <div className="gl-see">
          <span>See also</span>
          {related.map((r) => (
            <button key={r.id} type="button" onClick={() => onPick(r.id)}>
              {r.term}
            </button>
          ))}
        </div>
      )}
    </article>
  );
}

export function Glossary(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();

  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [essentialsOnly, setEssentialsOnly] = useState(false);
  const [letter, setLetter] = useState('');
  /** The term just jumped to, briefly highlighted so the eye finds it. */
  const [flash, setFlash] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const searching = query.trim().length > 0;

  const results = useMemo(
    () => searchGlossary({ text: query, category, essentialsOnly, letter }),
    [query, category, essentialsOnly, letter],
  );

  /**
   * Jump to a term, clearing whatever filter is hiding it.
   *
   * ⚠️ CLEARING THE FILTERS IS THE WHOLE POINT. A "see also" link from an entry
   * in one category almost always points at a term in another — additional
   * insured to certificate holder, subrogation to hold harmless. Following one
   * while a category filter is on would scroll to nothing.
   */
  const jumpTo = useCallback(
    (id: string) => {
      if (!glossaryTerm(id)) return;
      setQuery('');
      setCategory('');
      setLetter('');
      setEssentialsOnly(false);
      setFlash(id);
      navigate(`${location.pathname}#${id}`, { replace: true });
    },
    [navigate, location.pathname],
  );

  /*
    Scroll to whatever the URL hash names, after the filters above have been
    cleared and the list re-rendered. Two frames rather than one because the
    first paint after a filter change may not have the element yet.
  */
  useEffect(() => {
    const id = location.hash.replace(/^#/, '');
    if (!id) return;
    let raf = 0;
    raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    });
    setFlash(id);
    const t = window.setTimeout(() => setFlash(''), 2200);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
    };
  }, [location.hash, results]);

  /** Browsing groups by initial letter; searching does not. */
  const grouped = useMemo(() => {
    if (searching) return null;
    const out: { letter: string; terms: GlossaryTerm[] }[] = [];
    for (const t of results) {
      const c = t.term[0]!.toUpperCase();
      const last = out[out.length - 1];
      if (last && last.letter === c) last.terms.push(t);
      else out.push({ letter: c, terms: [t] });
    }
    return out;
  }, [results, searching]);

  const clearAll = () => {
    setQuery('');
    setCategory('');
    setLetter('');
    setEssentialsOnly(false);
  };
  const filtered = Boolean(query || category || letter || essentialsOnly);

  return (
    <div className="wrap">
      <PageHero
        title="Glossary"
        sub={`${GLOSSARY.length} commercial insurance terms — what each one means, the part that trips people up, and the term it gets confused with.`}
        action={
          <button
            className="btn btn-primary btn-sm"
            type="button"
            onClick={() => {
              setEssentialsOnly(true);
              setCategory('');
              setLetter('');
              setQuery('');
              searchRef.current?.blur();
            }}
          >
            Start here ({ESSENTIAL_COUNT})
          </button>
        }
      />

      <div className="gl-layout">
        {/*
          ⚠️ A RAIL RATHER THAN CHIPS, BECAUSE THERE ARE SIXTEEN CATEGORIES. The
          chip row that works for the Requests Center's three request types
          becomes a wall at this count. A vertical list stays scannable and has
          room for each category's own one-line description.
        */}
        <aside className="gl-rail">
          <button
            type="button"
            className={!category && !essentialsOnly ? 'is-on' : ''}
            onClick={() => {
              setCategory('');
              setEssentialsOnly(false);
            }}
          >
            <b>All terms</b>
            <span className="gl-n">{GLOSSARY.length}</span>
          </button>
          <button
            type="button"
            className={essentialsOnly ? 'is-on' : ''}
            onClick={() => {
              setEssentialsOnly(true);
              setCategory('');
            }}
          >
            <b>Start here</b>
            <span className="gl-n">{ESSENTIAL_COUNT}</span>
          </button>
          <p className="gl-rail-note">
            New to commercial lines? “Start here” is the short list worth reading before anything
            else.
          </p>
          <hr />
          {GLOSSARY_CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              className={category === c.id ? 'is-on' : ''}
              onClick={() => {
                setCategory(category === c.id ? '' : c.id);
                setEssentialsOnly(false);
              }}
              title={c.blurb}
            >
              <b>{c.label}</b>
              <span className="gl-n">{CATEGORY_COUNTS[c.id] ?? 0}</span>
            </button>
          ))}
        </aside>

        <section className="gl-main">
          {/*
            ⚠️ AT THE TOP, NOT IN A FOOTNOTE (Josh, 2026-09-05: "I think we
            should make it prominent on the page what you said about these being
            what terms generally mean"). It was at the bottom, which is exactly
            where the reader who most needs it — somebody new, looking a word up
            mid-email — never gets to. A glossary read by a new agent is where
            "it says here general liability covers that" starts, and the honest
            answer has to arrive before the definitions do, not after them.
          */}
          <div className="gl-caveat" role="note">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3a9 9 0 100 18 9 9 0 000-18zM12 11v5M12 8h.01" />
            </svg>
            <p>
              <b>These are what terms generally mean across the industry.</b> Any particular policy
              can define a word differently, and the wording in front of you is what decides a
              claim — not this page. When it matters, read the form or ask the commercial lines
              desk.
            </p>
          </div>

          <div className="gl-search">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-4-4" />
            </svg>
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setLetter('');
              }}
              placeholder="Search a term, an abbreviation, or what it does — COI, retro date, who pays first…"
              aria-label="Search the glossary"
            />
          </div>

          {/*
            The A–Z strip is a browse control, so it is hidden while searching —
            it would fight the ranking and it answers a question nobody has
            while typing.
          */}
          {!searching && (
            <div className="gl-az" role="group" aria-label="Jump to a letter">
              <button
                type="button"
                className={!letter ? 'is-on' : ''}
                onClick={() => setLetter('')}
              >
                All
              </button>
              {GLOSSARY_LETTERS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={letter === c ? 'is-on' : ''}
                  onClick={() => setLetter(letter === c ? '' : c)}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          <p className="gl-count">
            {results.length === GLOSSARY.length
              ? `${GLOSSARY.length} terms`
              : `${results.length} of ${GLOSSARY.length} terms`}
            {category && <> · {CATEGORY_LABEL[category as keyof typeof CATEGORY_LABEL]}</>}
            {essentialsOnly && <> · Start here</>}
            {filtered && (
              <button type="button" className="gl-clear" onClick={clearAll}>
                Clear
              </button>
            )}
          </p>

          {!results.length ? (
            <p className="lr-empty">
              Nothing matches “{query}”.
              <span>
                Try the abbreviation on its own, or a word from the definition — the search reads
                both.
              </span>
            </p>
          ) : searching ? (
            <div className="gl-list">
              {results.map((t) => (
                <Entry key={t.id} term={t} onPick={jumpTo} flash={flash === t.id} />
              ))}
            </div>
          ) : (
            grouped?.map((g) => (
              <div key={g.letter} className="gl-group">
                <h2 className="gl-letter">{g.letter}</h2>
                <div className="gl-list">
                  {g.terms.map((t) => (
                    <Entry key={t.id} term={t} onPick={jumpTo} flash={flash === t.id} />
                  ))}
                </div>
              </div>
            ))
          )}

        </section>
      </div>
    </div>
  );
}
